import { copyTextToClipboard } from "@oysterhr/common"
import { Button, Icon, Modal, Popover, Text, Toast, useToast } from "@oysterhr/ui"
import { captureMessage } from "@sentry/react"
import ToggleButton from "~/app/components/ToggleButton"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"
import { SalaryCalculation } from "@oysterhr/schema"
import { ReactNode, useState } from "react"
import { amplitudeLogEvent } from "~/app/utils/amplitudeHelpers"
import classNames from "classnames"
import { QuoteCycle } from "../../types"
import { useCalculationsProvider } from "../../contexts"
import { useCalculationsParams } from "../../hooks"

type CSVData = {
  base_currency_code: string
  calculations: Array<{
    country_code: string
    currency_code: string
    annual_salary: number
  }>
}

/**
 * The CSV API call takes into a specific data type to be able to export a CSV. This function
 * transforms the data from the calculations into the correct format.
 */
const transformCalculationsIntoCSVData = (
  calculations: SalaryCalculation[],
  baseCurrencyCode: string,
): CSVData => {
  return {
    base_currency_code: baseCurrencyCode,
    calculations: calculations.map(calculation => ({
      country_code: calculation.country.code,
      currency_code: calculation.currency.code,
      annual_salary: calculation.annualGrossSalary,
      state_or_province: calculation.stateOrProvince,
      vat_parameters: calculation.vatParameters
        ? {
            works_in_country_of_residence: calculation.vatParameters.worksInCountryOfResidence,
            sales_to_country: calculation.vatParameters.salesToCountry,
            sales_percentage_to_country: calculation.vatParameters.salesPercentageToCountry,
          }
        : undefined,
    })),
  }
}

export const CalculationsActions = () => {
  const { getParam } = useCalculationsParams()
  const { cycle, setCycle, calculations, eventPrefix, setCalculations } = useCalculationsProvider()
  const { t } = useTranslation("CostCalculator")
  const toast = useToast()

  const [isActionPopoverOpen, setIsActionPopoverOpen] = useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)

  const cycleOptions = [
    { label: t("annually"), value: "annual" },
    { label: t("monthly"), value: "monthly" },
  ]

  const shareResultsEvent = eventPrefix
    ? `${eventPrefix}-share_results-customer-onboarding`
    : undefined

  // Temporarily commented out until the feature is released
  const exportToCSV = async () => {
    const fileName = `Oyster_Cost_Calculator_${DateTime.now().toISODate()}`
    const data = transformCalculationsIntoCSVData(calculations, getParam("totalCurrency") ?? "EUR")
    try {
      const response = await fetch("/cost-calculator/download-sheet", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      const blob = await response.blob()

      const a = document.createElement("a")
      a.href = window.URL.createObjectURL(blob)
      a.download = fileName
      a.click()
      setIsActionPopoverOpen(false)
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>{t("flashMessage.exportToCSV.success")}</Toast.Content>
          </Toast>
        ),
      })
    } catch {
      captureMessage("Error exporting the CSV file in the new Cost Calculator")
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{t("flashMessage.exportToCSV.error")}</Toast.Content>
          </Toast>
        ),
      })
    }
  }

  const copyLink = async () => {
    const link = `${document.location.origin}/cost-calculator/${document.location.search}`

    try {
      await copyTextToClipboard(link)
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>{t("flashMessage.copyLink.success")}</Toast.Content>
          </Toast>
        ),
      })

      if (shareResultsEvent) {
        amplitudeLogEvent(shareResultsEvent)
      }
      setIsActionPopoverOpen(false)
    } catch (e) {
      console.log(e)
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{t("flashMessage.copyLink.error")}</Toast.Content>
          </Toast>
        ),
      })
    }
  }

  const handleResetCLick = () => {
    setIsResetModalOpen(true)
    setIsActionPopoverOpen(false)
  }

  const resetCalculations = () => {
    setCalculations([])
    setIsResetModalOpen(false)
  }

  return (
    <div
      className={classNames(
        "mb-6 flex w-full  items-center justify-between lg:grid lg:grid-cols-3 lg:flex-row lg:space-y-0",
      )}
    >
      <div className="lg:cols-span-1 flex items-center justify-center space-x-2 lg:col-start-2">
        <Text size="small" className="hidden whitespace-nowrap lg:flex">
          {t("showCosts")}
        </Text>
        <ToggleButton
          size="sm"
          values={cycleOptions}
          onChange={value => {
            setCycle(value as QuoteCycle)
          }}
          defaultValue={cycle}
          className={classNames("h-9 leading-4 lg:h-8 lg:leading-3")}
        />
      </div>
      <div className={classNames("flex flex-row items-center justify-center lg:justify-end")}>
        <Popover
          open={isActionPopoverOpen}
          onOpenChange={setIsActionPopoverOpen}
          placement="bottom-start"
        >
          <Popover.Trigger>
            <Button
              onClick={() => setIsActionPopoverOpen(prev => !prev)}
              size="sm"
              variation="secondary"
              startAdornment={<Icon name="DotsThreeVertical" />}
            >
              {t("actionsButtonLabel")}
            </Button>
          </Popover.Trigger>
          <Popover.Content className="flex flex-col">
            <ListItem
              label={t("exportAsCSV")}
              startAdornment={<Icon name="Export" />}
              onClick={exportToCSV}
              dataAnalyticsName="cost-calculator-export-xlsx"
            />
            <ListItem
              label={t("shareQuote")}
              startAdornment={<Icon name="ShareNetwork" />}
              onClick={copyLink}
              dataAnalyticsName="cost-calculator-share-estimates"
            />
            <ListItem
              label={t("reset")}
              startAdornment={<Icon name="CounterClockwise" />}
              onClick={handleResetCLick}
            />
          </Popover.Content>
        </Popover>
      </div>
      <Modal isOpen={isResetModalOpen} onRequestClose={() => setIsResetModalOpen(false)}>
        <Modal.Header>{t("resetModal.header")}</Modal.Header>
        <Modal.Content>
          <Text>{t("resetModal.content")}</Text>
        </Modal.Content>
        <Modal.Footer>
          <Button variation="primary" onClick={resetCalculations}>
            {t("confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

interface ListItemProps {
  startAdornment: ReactNode
  label: string
  onClick: () => void
  dataAnalyticsName?: string
}

const ListItem = ({ startAdornment, label, onClick, dataAnalyticsName }: ListItemProps) => {
  return (
    <button
      className="flex w-[200px] flex-row items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-400 hover:bg-gray-100"
      onClick={onClick}
      data-analytics-name={dataAnalyticsName}
    >
      {startAdornment}
      {label}
    </button>
  )
}
